import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Dashboard.css';
import placeholderImage from '../../../assets/placeholder.png';
import AddPropertyForm from '../../property/AddProperty/AddPropertyForm';
import PropertyCard from '../../property/PropertyCard/PropertyCard';
import { toast } from 'react-toastify';
import SkeletonLoader from '../../shared/SkeletonLoader/SkeletonLoader';
import EmptyState, { FilteredEmptyState } from '../../shared/EmptyState/EmptyState';
import { FaTimes, FaBuilding, FaClipboardCheck, FaClock, FaExclamationTriangle } from 'react-icons/fa';
import MainHeader from '../../shared/MainHeader/MainHeader';
import PageHeader from '../../shared/PageHeader/PageHeader';
import { StatCard } from '../../shared/SectionCard/SectionCard';
import ViewToggle from '../../shared/ViewToggle/ViewToggle';
import PropertyMap from '../../shared/PropertyMap/PropertyMap';
import { getDashboardRoute, getUserRole, isViewer, getRoleFeatures } from '../../../utils/roleUtils';
import { getApiEndpoint } from '../../../config/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAddPropertyForm, setShowAddPropertyForm] = useState(false);
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [userName, setUserName] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [statusFilters, setStatusFilters] = useState([]); // ['good','info','warning','critical','unknown']
  const [showRecentProperties] = useState(false);
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('dash_view') || 'grid');

  // Redirect role-specific users to their own dashboards (contractor admin vs staff go to different dashboards)
  useEffect(() => {
    const role = getUserRole();
    if (role === 'cloud_staff') {
      navigate('/cloud-staff/dashboard', { replace: true });
      return;
    }
    if (role === 'contractor') {
      const dashboardRoute = getDashboardRoute(role);
      navigate(dashboardRoute, { replace: true });
      return;
    }
    if (role === 'building_manager') {
      navigate('/building-manager/dashboard', { replace: true });
      return;
    }
  }, [navigate]);

  // Load properties and recent properties
  useEffect(() => {
    const loadData = async () => {
      await fetchProperties();
      const storedRecent = localStorage.getItem('recentProperties');
      if (storedRecent) {
        setRecentProperties(JSON.parse(storedRecent));
      }
    };
    
    loadData();
  }, [location.pathname]);

  // Monitor recent properties state changes and clean up any "test api" properties
  useEffect(() => {
    const hasTestApi = recentProperties.some(p => p.name === 'test api');
    if (hasTestApi) {
      const cleaned = recentProperties.filter(p => p.name !== 'test api');
      setRecentProperties(cleaned);
      localStorage.setItem('recentProperties', JSON.stringify(cleaned));
    }
  }, [recentProperties]);

  // Fetch user name on mount
  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await fetch(getApiEndpoint('/api/users/profile'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const userData = await response.json();
          setUserName(`${userData.firstName} ${userData.lastName}`.trim() || 'User');
        } else {
          setUserName('User');
        }
      } catch (error) {
        setUserName('User');
      }
    };
    fetchUserName();
  }, []);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearchResults && !event.target.closest('.search-container') && !event.target.closest('.search-results-dropdown')) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  // Add this helper function
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return placeholderImage;
    if (imageUrl.startsWith('http')) return imageUrl;
    
    // If the URL already contains 'uploads/', use it as is
    if (imageUrl.includes('uploads/')) {
      return getApiEndpoint(`/api/${imageUrl}`);
    }
    // Otherwise, add 'uploads/' to the path
    return getApiEndpoint(`/api/uploads/${imageUrl}`);
  };

  const fetchProperties = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(getApiEndpoint('/api/properties'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch properties');

      const data = await response.json();

      // Fetch compliance summary for all properties in one call and merge
      let merged = data;
      try {
        const summaryRes = await fetch(getApiEndpoint('/api/properties/compliance-summary'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (summaryRes.ok) {
          const summary = await summaryRes.json();
          const byId = new Map(summary.map(s => [s.property_id, s]));
          merged = data.map(p => {
            const s = byId.get(p.id);
            if (!s) return p;
            return {
              ...p,
              total_assets: s.totalAssets,
              passed_count: s.passed,
              failed_count: s.failed,
              pending_count: s.pending,
              untested_count: s.untested,
              compliance_pct: s.compliancePct,
              compliance_state: s.status, // 'good' | 'info' | 'warning' | 'critical' | 'unknown'
            };
          });
        }
      } catch (_) { /* non-fatal — fall back to base property data */ }

      setProperties(merged);
      
      // Handle initial recent properties
      const storedRecent = localStorage.getItem('recentProperties');
      
      if (!storedRecent && data.length > 0) {
        // Initialize with first 4 properties if no recent properties exist
        // BUT EXCLUDE "test api" property specifically
        const filteredData = data.filter(p => p.name !== 'test api');
        const initialRecent = filteredData.slice(0, Math.min(4, filteredData.length)).map(p => ({
          id: p.id,
          name: p.name,
          address: p.address,
          image_url: p.image_url
        }));
        localStorage.setItem('recentProperties', JSON.stringify(initialRecent));
        setRecentProperties(initialRecent);
      } else if (storedRecent) {
        // Load existing recent properties and ensure we have 4 if possible
        let recentProps = JSON.parse(storedRecent);
        
        // Filter out any properties that no longer exist in the main list
        recentProps = recentProps.filter(recentProp => 
          data.some(prop => prop.id === recentProp.id)
        );
        
        // If we have less than 4 recent properties, add more from the main list
        // BUT EXCLUDE "test api" property specifically
        if (recentProps.length < 4 && data.length > recentProps.length) {
          const existingIds = new Set(recentProps.map(p => p.id));
          const additionalProps = data
            .filter(p => !existingIds.has(p.id) && p.name !== 'test api') // EXCLUDE test api
            .slice(0, 4 - recentProps.length)
            .map(p => ({
              id: p.id,
              name: p.name,
              address: p.address,
              image_url: p.image_url
            }));
          
          recentProps = [...recentProps, ...additionalProps];
          localStorage.setItem('recentProperties', JSON.stringify(recentProps));
        }
        
        setRecentProperties(recentProps);
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
      toast.error('Failed to load properties. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);
    
    // Apply filters to properties
    let filtered = [...properties];
    
    if (newFilters.status && newFilters.status !== 'all') {
      // Filter by test status
      filtered = filtered.filter(property => {
        // This would need actual test status data from the backend
        return true; // Placeholder
      });
    }
    
    if (newFilters.assetType) {
      // Filter by asset type
      filtered = filtered.filter(property => {
        // This would need actual asset type data
        return true; // Placeholder
      });
    }
    
    if (newFilters.area) {
      // Filter by safety area
      filtered = filtered.filter(property => {
        // This would need actual area data
        return true; // Placeholder
      });
    }
    
    setFilteredProperties(filtered);
    
    // Save filters to localStorage for persistence
    localStorage.setItem('dashboard_filters', JSON.stringify(newFilters));
  };

  // Export functionality removed from dashboard per design update

  const addProperty = async (propertyData) => {
    try {
        // Add to main properties list
        setProperties(prevProperties => [...prevProperties, propertyData.property]);
        
        // Add to recent properties if we have less than 4
        const storedRecent = localStorage.getItem('recentProperties');
        let recentProps = storedRecent ? JSON.parse(storedRecent) : [];
        
        if (recentProps.length < 4) {
          const propertyToStore = {
            id: propertyData.property.id,
            name: propertyData.property.name,
            address: propertyData.property.address,
            image_url: propertyData.property.image_url
          };
          recentProps.push(propertyToStore);
          localStorage.setItem('recentProperties', JSON.stringify(recentProps));
          setRecentProperties(recentProps);
        }
        
        toast.success('Property added successfully');
        setShowAddPropertyForm(false);
    } catch (error) {
        console.error('Error adding property:', error);
        toast.error(error.message || 'Failed to add property');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(getApiEndpoint(`/api/properties/${propertyId}`), {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to delete property');
        }

        // Update main properties list
        setProperties(properties.filter(p => p.id !== propertyId));
        
        // Update recent properties
        const storedRecent = localStorage.getItem('recentProperties');
        if (storedRecent) {
            const recentProps = JSON.parse(storedRecent).filter(p => p.id !== propertyId);
            localStorage.setItem('recentProperties', JSON.stringify(recentProps));
            setRecentProperties(recentProps);
        }

        toast.success('Property deleted successfully');
    } catch (error) {
        console.error('Error deleting property:', error);
        toast.error(error.message || 'Failed to delete property');
    }
  };

  // Search functionality
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (!term.trim()) {
      setFilteredProperties([]);
      setShowSearchResults(false);
      return;
    }

    const filtered = properties.filter(property => 
      property.name.toLowerCase().includes(term) ||
      property.address.toLowerCase().includes(term) ||
      (property.description && property.description.toLowerCase().includes(term))
    );

    setFilteredProperties(filtered);
    setShowSearchResults(true);
  };

  const updateRecentProperties = (propertyId) => {
    // Find the property in our list
    const property = properties.find(p => p.id === propertyId);
    if (!property) {
      return;
    }
    
    // BLOCK "test api" property from being added to recent properties
    if (property.name === 'test api') {
      return;
    }
    
    // Check if test api is permanently banned
    const isTestApiBanned = localStorage.getItem('banTestApi') === 'true';
    if (isTestApiBanned && property.name === 'test api') {
      return;
    }

    // Get current recent properties
    const currentRecent = JSON.parse(localStorage.getItem('recentProperties') || '[]');
    
    // Create new property object
    const newProperty = {
      id: property.id,
      name: property.name,
      address: property.address,
      image_url: property.image_url
    };
    
    // Remove the property if it already exists
    const filteredRecent = currentRecent.filter(p => p.id !== propertyId);
    
    // Add the new property to the front
    const updatedRecent = [newProperty, ...filteredRecent];
    
    // Keep only 4 most recent properties
    const finalRecent = updatedRecent.slice(0, 4);
    
    // Update localStorage and state
    localStorage.setItem('recentProperties', JSON.stringify(finalRecent));
    setRecentProperties(finalRecent);
  };

  const handlePropertyClick = (propertyId) => {
    updateRecentProperties(propertyId);
    navigate(`/propertydashboard/${propertyId}`);
  };

  const handleViewChange = (mode) => {
    setViewMode(mode);
    sessionStorage.setItem('dash_view', mode);
  };

  const triggerGeocoding = useCallback(() => {
    fetchProperties();
  }, []);


  // Render the recent properties section
  const RecentPropertiesSection = () => (
    <div className="recent-properties">
      <div className="recent-properties-header">
        <h2 className="recent-properties-title">Recently Viewed Properties</h2>
      </div>
      <div className="recent-properties-grid">
        {recentProperties.map((property) => (
          <div 
            key={property.id} 
            className="recent-property-card"
            onClick={() => handlePropertyClick(property.id)}
            style={{ cursor: 'pointer' }}
          >
            <img 
              src={getImageUrl(property.image_url)}
              alt={property.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = placeholderImage;
              }}
              className="recent-property-image" 
            />
            <div className="recent-property-info">
              <h3 className="recent-property-name">{property.name}</h3>
              <p className="recent-property-address">{property.address}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const viewOnly = isViewer();
  const canEdit = getRoleFeatures().canEditProperties;
  const baseList = searchTerm ? filteredProperties : properties;
  const displayProperties = statusFilters.length === 0
    ? baseList
    : baseList.filter(p => statusFilters.includes(p.compliance_state || 'unknown'));
  const toggleStatusFilter = (val) =>
    setStatusFilters(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const totalProperties = properties.length;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="dash-page">
        <MainHeader />
        <main className="dash-content">
          <div className="dash-skeleton-header">
            <div className="skeleton skeleton-text" style={{ width: '280px', height: '1.75rem', marginBottom: '8px' }} />
            <div className="skeleton skeleton-text" style={{ width: '200px', height: '1rem' }} />
          </div>
          <div className="dash-stats-row">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="skeleton dash-stat-skeleton" />
            ))}
          </div>
          <div className="skeleton" style={{ width: '100%', height: '48px', borderRadius: '8px', marginBottom: '24px' }} />
          <div className="dash-grid">
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonLoader key={i} type="property-card" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <MainHeader />

      <main className="dash-content">
        {/* Page Header */}
        <PageHeader
          title={`Welcome back, ${userName}`}
          subtitle={
            viewOnly
              ? "Here's an overview of your properties (view only)"
              : "Here's an overview of your properties"
          }
          actions={
            !viewOnly && (
              <button
                className="dash-btn-primary"
                onClick={() => setShowAddPropertyForm(true)}
                type="button"
              >
                <FaBuilding />
                Add Property
              </button>
            )
          }
        />

        {/* Stats Row */}
        <section className="dash-stats-row" aria-label="Property statistics">
          <StatCard
            value={totalProperties}
            label="Total Properties"
            icon={<FaBuilding />}
            color="blue"
          />
          <StatCard
            value={properties.filter(p => p.compliance_status === 'compliant').length || 0}
            label="Compliant"
            icon={<FaClipboardCheck />}
            color="green"
          />
          <StatCard
            value={properties.filter(p => p.compliance_status === 'due-soon').length || 0}
            label="Due Soon"
            icon={<FaClock />}
            color="amber"
          />
          <StatCard
            value={properties.filter(p => p.compliance_status === 'overdue').length || 0}
            label="Overdue"
            icon={<FaExclamationTriangle />}
            color="red"
          />
        </section>

        {/* Search Bar */}
        <div className="dash-search" role="search" aria-label="Search properties">
          <div className="dash-search-bar">
            <i className="fas fa-search dash-search-icon" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search by property name or address..."
              value={searchTerm}
              onChange={handleSearch}
              className="dash-search-input"
              aria-label="Search properties"
            />
            {searchTerm && (
              <button
                className="dash-search-clear"
                onClick={() => { setSearchTerm(''); setFilteredProperties([]); setShowSearchResults(false); }}
                aria-label="Clear search"
                type="button"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        {/* Status filter chips */}
        <div className="dash-filter-bar" role="group" aria-label="Filter by compliance status">
          {[
            { v: 'good',     l: 'Compliant' },
            { v: 'info',     l: 'Review' },
            { v: 'warning',  l: 'Investigate' },
            { v: 'critical', l: 'Action Required' },
            { v: 'unknown',  l: 'No Data' },
          ].map(({ v, l }) => (
            <button
              key={v}
              type="button"
              className={`dash-filter-chip ${statusFilters.includes(v) ? `dash-filter-chip-active dash-filter-chip-${v}` : ''}`}
              onClick={() => toggleStatusFilter(v)}
            >
              {l}
            </button>
          ))}
          {statusFilters.length > 0 && (
            <button
              type="button"
              className="dash-filter-clear"
              onClick={() => setStatusFilters([])}
            >
              Clear
            </button>
          )}
        </div>

        {/* Recently Viewed — disabled via feature flag */}
        {showRecentProperties && recentProperties.length > 0 && <RecentPropertiesSection />}

        {/* Properties Section */}
        <section className="dash-properties-section" aria-label="Properties">
          <div className="dash-section-header">
            <h2 className="dash-section-title">
              {searchTerm ? `Results for "${searchTerm}"` : 'Your Properties'}
              {!searchTerm && totalProperties > 0 && (
                <span className="dash-section-count">{totalProperties}</span>
              )}
            </h2>
            {displayProperties.length > 0 && (
              <ViewToggle view={viewMode} onViewChange={handleViewChange} />
            )}
          </div>

          {error ? (
            <EmptyState
              type="error"
              title="Failed to Load Properties"
              description={error}
              actionLabel="Retry"
              onAction={fetchProperties}
            />
          ) : (searchTerm && filteredProperties.length === 0) ? (
            <FilteredEmptyState
              searchTerm={searchTerm}
              filters={activeFilters}
              onClearFilters={() => handleFilterChange({})}
              onClearSearch={() => { setSearchTerm(''); setFilteredProperties([]); }}
            />
          ) : displayProperties.length === 0 ? (
            viewOnly ? (
              <EmptyState
                type="no-properties"
                title="No properties assigned"
                description="You don't have any properties yet. Contact your administrator or property manager to get access."
              />
            ) : (
              <EmptyState
                type="no-properties"
                actionLabel="Add Your First Property"
                onAction={() => setShowAddPropertyForm(true)}
              />
            )
          ) : viewMode === 'map' ? (
            <PropertyMap
              properties={displayProperties}
              onPropertyClick={handlePropertyClick}
              triggerGeocoding={triggerGeocoding}
            />
          ) : (
            <div className="dash-grid">
              {displayProperties.map(property => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onDelete={canEdit ? handleDeleteProperty : null}
                  onPropertyClick={handlePropertyClick}
                  viewOnly={viewOnly}
                />
              ))}
            </div>
          )}
        </section>

        {/* Add Property Modal */}
        {showAddPropertyForm && !viewOnly && (
          <AddPropertyForm
            onClose={() => setShowAddPropertyForm(false)}
            onAddProperty={addProperty}
          />
        )}
      </main>
    </div>
  );
};

export default Dashboard;
